// Orders list page render (with its filter toolbar). Takes `orders`,
// `products`, `orderFilter` as explicit arguments (same dependency-injection
// pattern as the other extracted page/service modules) rather than closing
// over the app's mutable ORDERS/PRODUCTS/ORDER_FILTER globals. `orderFilter`
// is read-only here — this function only *displays* the current filter
// state, it never mutates it (that's setFilter()/resetFilter(), still
// inline — they mutate ORDER_FILTER and call render(), a different category
// of function than a pure HTML-string builder, see
// REFACTOR_SECURITY_REPORT.md, Phase 2 section).
//
// selFilter/uniqueVals/uniqueProducts/filteredOrders/hasFlags moved here as
// module-private helpers — grep-confirmed none of them is called from
// anywhere else in public/index.html.
//
// Every onclick="..."/oninput="..." string in the returned HTML
// (openOrder(...), setFilter(...), moreActions(...), ...) is untouched —
// this only moves what builds the string.
import { esc, money, fmtDate } from '../utils/format.js';
import { ic } from '../utils/icons.js';
import { norm } from '../utils/text.js';
import { STATUSES, statusMap, badge, paymentPill, productDisplayHtml } from '../components/labels.js';
import { getOrderItems, orderLabelText, orderTotal, productsLabel } from '../services/pricing.js';
import { byNew } from '../services/orders.js';

function hasFlags(o) {
  return o.verify && Object.values(o.verify).some(Boolean);
}

function selFilter(key, label, opts, val) {
  let h = `<select class="inp" onchange="setFilter('${key}',this.value)"><option value="">${label} : tous</option>`;
  opts.forEach(([v, l]) => { h += `<option value="${esc(v)}" ${val === v ? 'selected' : ''}>${esc(l)}</option>`; });
  return h + '</select>';
}

function uniqueVals(orders, key) {
  return [...new Set(orders.map(o => o[key]).filter(Boolean))].sort();
}

function uniqueProducts(orders, products) {
  return [...new Set(orders.flatMap(o => [String(o.orderLabel || '').trim(), ...getOrderItems(products, o).map(item => item.product)]).filter(Boolean))].sort();
}

function filteredOrders(orders, products, orderFilter) {
  const f = orderFilter, q = norm(f.q);
  return [...orders].sort(byNew).filter(o => {
    if (f.status && o.status !== f.status) return false;
    if (Array.isArray(f.statusGroup) && f.statusGroup.length && !f.statusGroup.includes(o.status)) return false;
    if (f.city && o.city !== f.city) return false;
    if (f.product && String(o.orderLabel || '').trim() !== f.product && !getOrderItems(products, o).some(item => item.product === f.product)) return false;
    if (q) {
      const b = norm([o.name, o.phone, o.phone2, o.orderNo, o.city, orderLabelText(products, o), productsLabel(products, o), o.trackingNo, o.address].join(' '));
      if (!b.includes(q)) return false;
    }
    return true;
  });
}

export function viewOrders(orders, products, orderFilter) {
  const f = orderFilter;
  let h = '<div class="toolbar">';
  h += `<div class="search"><span class="si">${ic('search', 16)}</span><input class="inp" id="fq" placeholder="Nom, téléphone, ville ou n°" aria-label="Rechercher une commande" value="${esc(f.q)}" oninput="setFilter('q',this.value)"></div>`;
  h += selFilter('status', 'Statut', STATUSES.map(s => [s.k, s.l]), f.status);
  h += selFilter('city', 'Ville', uniqueVals(orders, 'city').map(v => [v, v]), f.city);
  h += selFilter('product', 'Produit', uniqueProducts(orders, products).map(v => [v, v]), f.product);
  h += `<button class="btn ghost sm" onclick="resetFilter()">Réinitialiser</button><button class="btn sm" onclick="exportCSV()">${ic('download', 15)}CSV</button>`;
  h += '</div>';
  const list = filteredOrders(orders, products, orderFilter);
  h += '<div class="hint" style="margin:-4px 0 10px">' + list.length + ' commande(s) affichée(s)' + (Array.isArray(f.statusGroup) && f.statusGroup.length ? ' · <span class="chip">' + f.statusGroup.map(k => statusMap[k]?.l || k).join(' + ') + '</span>' : '') + '</div><div class="card"><div class="bd" style="padding:0">';
  if (!list.length) h += '<div class="empty">Aucune commande.</div>';
  else {
    h += '<div class="table-wrap desktop-orders"><table><thead><tr><th>Client</th><th>Ville</th><th>Produit</th><th>À encaisser</th><th>Paiement</th><th>Statut</th><th>Date</th><th>Actions</th></tr></thead><tbody>';
    list.forEach(o => { h += `<tr>
      <td class="bidi"><b>${esc(o.name || '—')}</b>${hasFlags(o) ? '<span class="tag-verify">' + ic('alert', 11) + 'À vérifier</span>' : ''}<br><span class="hint">${esc(o.orderNo)} · ${esc(o.phone || '')}</span></td>
      <td>${esc(o.city || '—')}</td>
      <td class="bidi">${productDisplayHtml(o, products)}</td>
      <td><b>${money(orderTotal(products, o))}</b></td>
      <td>${paymentPill(o.paymentStatus)}</td>
      <td>${badge(o.status)}</td>
      <td>${fmtDate(o.orderDate)}</td>
      <td><div class="qa"><button class="btn sm ghost" onclick="openOrder('${o.id}')">${ic('eye', 14)}Voir</button>${o.status === 'livree' && o.paymentStatus !== 'received' ? `<button class="btn sm green" onclick="openOrderPayment('${o.id}')">Encaisser</button>` : ''}<button class="iconbtn" aria-label="Autres actions pour ${esc(o.name || o.orderNo)}" title="Autres actions" onclick="moreActions('${o.id}')">${ic('more', 15)}</button></div></td></tr>`; });
    h += '</tbody></table></div><div class="mobile-order-list">' + list.map(o => `<article class="mobile-order-card"><div class="mobile-order-head"><div><b>${esc(o.name || '—')}</b><div class="hint">${esc(o.orderNo)} · ${esc(o.city || '—')}</div></div>${badge(o.status)}</div><div style="margin-top:9px">${productDisplayHtml(o, products)}</div><div style="display:flex;justify-content:space-between;gap:10px;margin-top:9px"><b>${money(orderTotal(products, o))}</b>${paymentPill(o.paymentStatus)}</div><div class="mobile-order-actions"><button class="btn ghost" onclick="openOrder('${o.id}')">Voir</button>${o.status === 'livree' && o.paymentStatus !== 'received' ? `<button class="btn green" onclick="openOrderPayment('${o.id}')">Encaisser</button>` : `<button class="btn" onclick="moreActions('${o.id}')">Actions</button>`}</div></article>`).join('') + '</div>'; }
  h += '</div></div><p class="hint" style="margin-top:10px">' + list.length + ' commande(s) sur ' + orders.length + '.</p>';
  return h;
}
